import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, MapPin, Plane, TrainFront, ChevronRight, CalendarClock, CheckCircle2, XCircle } from "lucide-react";
import { RiderShell } from "@/components/Shells";
import StatusBadge, { isActiveStatus, effectiveStatus } from "@/components/StatusBadge";
import { EmptyState, Spinner } from "@/components/States";
import { api } from "@/lib/api";
import { money, fmtDateTime, cn } from "@/lib/format";

const TABS = [
  { key: "upcoming", label: "Upcoming", icon: CalendarClock },
  { key: "past", label: "Past", icon: CheckCircle2 },
  { key: "cancelled", label: "Cancelled", icon: XCircle },
];

const groupOf = (r) => {
  if (r.status === "cancelled") return "cancelled";
  if (r.status === "completed") return "past";
  return "upcoming";
};

export default function RiderTrips() {
  const navigate = useNavigate();
  const [rides, setRides] = useState(null);
  const [tab, setTab] = useState("upcoming");

  useEffect(() => {
    api("/me/rides").then((r) => setRides(r.rides || [])).catch(() => setRides([]));
  }, []);

  const groups = useMemo(() => {
    const g = { upcoming: [], past: [], cancelled: [] };
    for (const r of rides || []) g[groupOf(r)].push(r);
    return g;
  }, [rides]);

  const list = groups[tab] || [];

  return (
    <RiderShell>
      <div className="mx-auto max-w-2xl px-5 py-6">
        <h1 className="mb-4 text-2xl font-extrabold text-foreground">Your trips</h1>

        <div className="mb-5 flex gap-1 rounded-2xl bg-secondary p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`trips-tab-${t.key}`}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 text-sm font-semibold transition-colors",
                tab === t.key ? "bg-card text-brand-600 shadow-soft" : "text-muted-foreground"
              )}
            >
              <t.icon className="h-4 w-4" /> {t.label}
              <span className={cn("rounded-full px-1.5 text-xs", tab === t.key ? "bg-accent" : "bg-transparent")}>{groups[t.key].length}</span>
            </button>
          ))}
        </div>

        {rides === null ? (
          <div className="flex justify-center py-16"><Spinner className="h-7 w-7 text-brand-600" /></div>
        ) : list.length === 0 ? (
          <EmptyState icon={Clock} title={`No ${tab} rides`} subtitle={tab === "upcoming" ? "Book a ride to see it here." : `Your ${tab} rides will appear here.`} />
        ) : (
          <div className="space-y-3">
            {list.map((r) => {
              const openable = isActiveStatus(r.status);
              return (
                <button
                  key={r.id}
                  data-testid={`trip-${r.id}`}
                  onClick={() => openable && navigate(`/ride/${r.id}`)}
                  className={cn("flex w-full items-center gap-4 rounded-2xl border border-border bg-card p-4 text-left transition-colors", openable ? "hover:border-brand-500/50" : "cursor-default")}
                >
                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <StatusBadge status={effectiveStatus(r)} />
                      <span className="text-xs text-muted-foreground">{fmtDateTime(r.when === "scheduled" ? r.scheduled_ts : r.created_at)}</span>
                    </div>
                    <p className="flex items-center gap-1.5 truncate text-sm text-foreground">
                      {r.pickup.airport ? <Plane className="h-3.5 w-3.5 text-brand-600" /> : r.pickup.station ? <TrainFront className="h-3.5 w-3.5 text-brand-600" /> : <MapPin className="h-3.5 w-3.5 text-success" />}
                      <span className="truncate">{r.pickup.label}</span>
                    </p>
                    <p className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-muted-foreground">
                      {r.destination.airport ? <Plane className="h-3.5 w-3.5 text-brand-600" /> : r.destination.station ? <TrainFront className="h-3.5 w-3.5 text-brand-600" /> : <MapPin className="h-3.5 w-3.5 text-brand-600" />}
                      <span className="truncate">{r.destination.label}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-num font-bold text-foreground">{money(r.final_fare || r.recommended_fare)}</p>
                    {openable && <ChevronRight className="ml-auto mt-1 h-4 w-4 text-muted-foreground" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </RiderShell>
  );
}
